import { useQuery } from "@tanstack/react-query";
import { refetchApiInterval } from "../../shared/constants";
import useGlobalStore from "../../store/useGlobalStore";
import { getProjectAssetTrendsDetails } from "../api/power-trends-service";
import { getAssetSocChartDetailsData, getAssetTrendsDetailsV2Range, getEssAssetEventsRange, getMapDirectionData, getMethanolAssetEvents, getProjectAssetChargeActivitiesData, getProjectAssetChargeActivitiesDataWithAssetId, getProjectAssetsData, getProjectEnergyActivitiesData, getProjectEnergyMetricsData, getProjectListData, getProjectNetworkData, getProjectProductivityData, getUsageHistoryDetailsData } from "../api/project-asset-details.service";
const MAX_PROJECT_LIST_PAGES = 10;
const updateProjectListCursor = (queryParams, nextCursor) => {
    if (!nextCursor?.trim()) {
        return queryParams;
    }
    const searchParams = new URLSearchParams(queryParams);
    searchParams.set("cursor", nextCursor);
    return searchParams.toString();
};
export const useProjectListQuery = (queryParams, partyNumber, fetchAllPages = false) => {
    return useQuery({
        enabled: !!partyNumber,
        gcTime: 0,
        queryFn: async () => {
            if (!fetchAllPages) {
                return getProjectListData(queryParams);
            }
            let currentQueryParams = queryParams;
            const aggregatedProjects = [];
            let latestResponse = { projects: [] };
            let nextCursor;
            const seenCursors = new Set();
            let pageCount = 0;
            do {
                pageCount += 1;
                if (pageCount > MAX_PROJECT_LIST_PAGES) {
                    console.warn("Project list pagination exceeded maximum page count");
                    nextCursor = undefined;
                    latestResponse = {
                        ...latestResponse,
                        responseMetadata: { ...latestResponse.responseMetadata, nextCursor: undefined }
                    };
                    break;
                }
                latestResponse = await getProjectListData(currentQueryParams);
                aggregatedProjects.push(...(latestResponse.projects ?? []));
                nextCursor = latestResponse.responseMetadata?.nextCursor?.trim() || undefined;
                if (nextCursor) {
                    if (seenCursors.has(nextCursor)) {
                        console.warn("Project list pagination returned a repeated cursor");
                        nextCursor = undefined;
                        latestResponse = {
                            ...latestResponse,
                            responseMetadata: { ...latestResponse.responseMetadata, nextCursor: undefined }
                        };
                        break;
                    }
                    seenCursors.add(nextCursor);
                    currentQueryParams = updateProjectListCursor(queryParams, nextCursor);
                }
            } while (nextCursor);
            return {
                ...latestResponse,
                projects: aggregatedProjects,
                responseMetadata: { ...latestResponse.responseMetadata, nextCursor: undefined }
            };
        },
        queryKey: ["projectList", queryParams, fetchAllPages],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useProjectAssetsDetailsQuery = (id, queryParams, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: () => {
            return getProjectAssetsData(id, queryParams);
        },
        queryKey: ["projectAssetsDetails", id, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useUsageHistoryDetailsQuery = ({ aggregation, assetId, assetSourceType, fetchHistoryData, id, refetchApiData, refreshTimestamp, startDate }) => {
    const setIsLoading = useGlobalStore((state) => state.setIsUsageHistoryLoading);
    return useQuery({
        enabled: fetchHistoryData,
        gcTime: 0,
        queryFn: async () => {
            setIsLoading(true);
            const response = await getUsageHistoryDetailsData(id, startDate, aggregation, assetSourceType, assetId);
            setIsLoading(false);
            if (!response) {
                throw new Error("No Data Available");
            }
            return response;
        },
        queryKey: [
            "UsageHistory",
            startDate,
            aggregation,
            id,
            assetId,
            assetSourceType,
            refreshTimestamp,
            refetchApiData
        ],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useProjectProductivityQuery = ({ assetId, assetSourceType = "MACHINE", duration = "daily", enabled = true, id, refetchApiData, startDate }) => {
    return useQuery({
        enabled,
        gcTime: 0,
        queryFn: async () => {
            const response = await getProjectProductivityData(id, startDate, duration, assetSourceType, assetId);
            // Only a genuinely missing response is an error. An empty but present
            // payload (productivityDetails/activities === []) is a valid "no data for
            // this site/date" result and must flow through as success so the panel
            // shows its calm empty state ("No data available for this zone") rather
            // than the "Something went wrong" error state.
            if (!response) {
                throw new Error("No Data Available");
            }
            return response;
        },
        queryKey: ["ProjectProductivity", id, startDate, duration, assetSourceType, assetId, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useProjectEnergyMetricsQuery = (id, startDate, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectEnergyMetricsData(id, startDate);
        },
        queryKey: ["EnergyMetrics", startDate, id, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useProjectNetworkQuery = (id, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectNetworkData(id);
        },
        queryKey: ["projectNetwork", id, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useProjectPowerSocTrendsQuery = (projectId, startDate, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectAssetTrendsDetails(projectId, startDate);
        },
        queryKey: ["projectPowerSocTrends", projectId, startDate, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useAssetPowerSocTrendsQuery = (projectId, startDate, assetId, frequency, refetchApiData, fromDate, toDate, cursor) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getProjectAssetTrendsDetails(projectId, startDate, assetId, frequency, fromDate, toDate, cursor);
        },
        queryKey: [
            "assetPowerSocTrends",
            projectId,
            startDate,
            assetId,
            frequency,
            refetchApiData,
            fromDate,
            toDate,
            cursor
        ],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useProjectEnergyActivitiesQuery = (id, startDate, fetchHistoryData, refetchApiData) => {
    return useQuery({
        enabled: fetchHistoryData,
        gcTime: 0,
        queryFn: () => {
            return getProjectEnergyActivitiesData(id, startDate);
        },
        queryKey: ["EnergyActivities", startDate, id, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useProjectAssetChargeActivitiesQuery = (id, startDate, assetId, fetchHistoryData, refreshTimestamp, refetchApiData) => {
    const setIsLoading = useGlobalStore((state) => state.setIsLoading);
    return useQuery({
        enabled: fetchHistoryData,
        gcTime: 0,
        queryFn: async () => {
            setIsLoading(true);
            let response;
            if (assetId) {
                response = await getProjectAssetChargeActivitiesDataWithAssetId(id, startDate, assetId);
            }
            else {
                response = await getProjectAssetChargeActivitiesData(id, startDate);
            }
            setIsLoading(false);
            // Check if response has assetChargeActivities array
            if (!response?.assetChargeActivities?.length) {
                throw new Error("No Data Available");
            }
            return response;
        },
        queryKey: ["AssetChargeActivities", startDate, id, assetId, refreshTimestamp, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useMapDirectionQuery = (requestBody, projectId, refetchApiData) => {
    return useQuery({
        gcTime: 0,
        queryFn: async () => {
            return await getMapDirectionData(requestBody);
        },
        queryKey: ["mapDirection", projectId, JSON.stringify(requestBody), refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
export const useMethanolAssetEventsQuery = ({ assetId, enabled = true, fromDate, limit = 10, projectId, toDate }) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId && !!fromDate && !!toDate,
        queryFn: () => {
            if (!projectId || !assetId || !fromDate || !toDate) {
                throw new Error("Missing required parameters");
            }
            return getMethanolAssetEvents(projectId, assetId, fromDate, toDate, {
                limit: String(limit),
                skip: "0"
            });
        },
        queryKey: ["methanolAssetEvents", projectId, assetId, fromDate, toDate, limit],
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000
    });
};
export const useAssetSocChartQuery = (assetId, fetchIdleBurnSoc, refetchApiData) => {
    return useQuery({
        enabled: fetchIdleBurnSoc,
        gcTime: 0,
        queryFn: () => {
            return getAssetSocChartDetailsData(assetId);
        },
        queryKey: ["assetSocChartDetails", assetId, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
// Returns the complete activity range for the selected asset/date window.
// The underlying service follows cursor-based pagination and merges all pages
// so consumers receive a single fully-populated dataset.
export const useEssAssetEventsRangeQuery = (projectId, assetId, fromDate, toDate, enabled = true) => {
    return useQuery({
        enabled: enabled && !!projectId && !!assetId && !!fromDate && !!toDate,
        gcTime: 0,
        queryFn: () => getEssAssetEventsRange(projectId, assetId, fromDate, toDate),
        queryKey: ["essRangeActivities", projectId, assetId, fromDate, toDate],
        // Range queries can fan out into multiple paginated requests. Avoid automatic
        // React Query retries so pagination failures surface immediately.
        retry: false,
        staleTime: 5 * 60 * 1000
    });
};
// Returns the complete SoC trends dataset for the selected range.
// The underlying service exhausts cursor pagination and aggregates all trend
// pages before returning the response to chart consumers.
export const useAssetPowerSocTrendsV2Query = (projectId, assetId, fromDate, toDate, frequency, refetchApiData, cursor) => {
    return useQuery({
        enabled: !!projectId && !!assetId && !!fromDate && !!toDate,
        gcTime: 0,
        queryFn: async () => {
            return await getAssetTrendsDetailsV2Range(projectId, assetId, fromDate, toDate, frequency);
        },
        queryKey: ["assetPowerSocTrendsV2", projectId, assetId, fromDate, toDate, frequency, cursor, refetchApiData],
        refetchInterval: refetchApiInterval,
        retry: 1
    });
};
